import { useState } from "react";
import { Box, Chip, Paper, Tooltip, Typography } from "@mui/material";
import { LockOutlined as LockIcon } from "@mui/icons-material";
import { useGetList, useGetOne } from "react-admin";
import { getCarrierLabel } from "@/components/common/PolicyField";
import { BORDER_RADIUS } from "@/constants/layout";
import {
  Client,
  CompletenessCheck,
  DocumentRecordType,
  DocumentTransformation,
  ExtractedRecord,
  Policy,
  ResourceName,
  TransformationTemplate,
  User,
} from "@/types";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { ClientPickerDialog } from "./ClientPickerDialog";
import { ClassificationChip } from "./ClassificationChip";
import { DeductibleContextChips } from "./DeductibleContextChips";
import { ReportedAsOfControl } from "./ReportedAsOfControl";
import { ReviewActions } from "./ReviewActions";
import type { ReviewAsOfState } from "../../hooks/useReviewAsOfState";
import { HeaderTriggerButton } from "./HeaderTriggerButton";
import type { DocumentReviewProjection, RecordSummaryPayload, ResolvedPolicy } from "@/types/documentTransformation";
import type { DocumentClassification } from "../../hooks/useDocumentClassification";

interface ReviewContextBarProps {
  /** Still passed by callers; no longer rendered here since #7431 (tabs + footer carry the count). */
  totalRecords: number;
  recordType?: DocumentRecordType;

  // Header context cells (#7431 AC1). Client/Layout/Uploaded are new here;
  // Category and Paid through were already in the bar.
  /** Whether this document type needs a client at all (reimbursement BOB does not). */
  requiresClient: boolean;
  selectedClient: Client | null;
  onClientChange: (client: Client | null) => void;
  /** Matched transformation template — shown as the file "Layout". */
  template: TransformationTemplate | null | undefined;
  uploadedByUserId?: string;
  uploadedDatetime?: string;
  /**
   * Caller lacks documents:create (#7431 AC4). Locks the header (with
   * partnerReadOnly) and makes Approve say "View only. A reviewer approves
   * this file." Pass the ROLE flag only; the partner flag goes in partnerReadOnly.
   */
  readOnly?: boolean;
  /** Approval forecast still running (#7431 AC2 "Checking…"). */
  isChecking?: boolean;
  /** Active partner is read-only (ADR-035) — separate cause, separate copy. */
  partnerReadOnly?: boolean;
  /**
   * Paid-through (as-of) state, derived ONCE in DocumentReviewTab (ADR-045
   * 2026-07-28 amendment §2: useReviewAsOfState is the single home) and
   * shared with the sticky approve bar so both gates agree.
   */
  asOfState: ReviewAsOfState;
  extractedRecords?: ExtractedRecord[];
  reviewProjection?: DocumentReviewProjection | null;
  /**
   * The projection was skipped, refused (409) or failed, so resolved_policy
   * will never arrive (#7462, !4938 review). The Policy cell then falls back to
   * the client's newest policy — the rule resolvePolicyForClient applies — so
   * the largest files, where a reviewer most needs the context, keep the label.
   */
  projectionUnavailable?: boolean;

  /** Still passed by callers; coverage moved to the Mapping tab in #7431 and is no longer rendered here. */
  transformation: DocumentTransformation | null;

  // Classification chip
  classification: DocumentClassification | null;
  isClassificationLoading: boolean;
  isClassificationSaving: boolean;
  isClassificationReprocessing: boolean;
  classificationError: string | null;
  onClassificationOverride: (value: string | null) => Promise<void>;

  // Reviewer-confirmed paid-through (as-of) date.
  reportedAsOf: string;
  onAsOfChange: (value: string) => void;
  /** Provenance of reportedAsOf (#7439 AC4) — the popover's source line. */
  asOfSource?: "reviewer" | "file" | "defaulted" | "none";
  /** The rail is showing a paid-through card (#7439 AC1): warning tone + icon on the header button. */
  asOfFlagged?: boolean;

  // Review actions
  onReviewMapping: () => void;
  /** Re-run the document from source (#6920). Omitted when not permitted. */
  onReprocess?: () => void;
  /** Reviewer exclusions held on this screen — reprocess confirm copy only. */
  excludedCount?: number;
  /** True while a reprocess is already running — disables the control. */
  isReprocessing?: boolean;
  onApprove: () => void;
  /** Opens the reject dialog (#7441) — see ReviewActions.onReject. */
  onReject: () => void;
  /** Opens the delete flow; absent → "Delete document…" is disabled, "Admins only" (#7441). */
  onDeleteDocument?: () => void;
  isApproving: boolean;
  isRejecting: boolean;
  isReadyForApproval: boolean;
  hasTransformation: boolean;
  isClientMissing: boolean;
  errorCount: number;
  unresolvedErroredCount?: number;
  /** False when unresolvedErroredCount is an upper bound — see ReviewActions. */
  unresolvedCountIsExact?: boolean;
  /**
   * The transformation's server-computed record summary (#6903).
   *
   * Required and explicitly passed: it decides whether the paid-through-date
   * control renders, and a control that silently fails to render hands claims
   * authority to the carrier-reported side (ADR-051). See useReviewAsOfState.
   */
  recordSummary: RecordSummaryPayload | undefined;
  warningCount: number;
  mappingBlockingCount: number;
  completeness?: CompletenessCheck;
  mappingAdvisoryCount: number;
}

/**
 * "Summit Re 2026" — the header's policy label (#7431 AC1), shared with the
 * Deductible header tooltip and the approve confirm's subtitle (#7440 AC1).
 *
 * Built from the projection's `resolved_policy` (#7462): the policy the
 * server actually ran the forecast against, so the label, the Deductible
 * beside it and the out-of-term advisory describe one policy by construction.
 * This used to re-derive "newest by effective_start_date" from a client-side
 * policies query to MIRROR the server's choice (!4920 review) — agreement by
 * duplication, which drifts the moment the server's resolution changes
 * (#7436). Carrier + year, never aegle_id (policy display convention); "No
 * Carrier" when the policy names none, as the policies list does. Null when
 * the projection has not loaded or the client has no policy.
 */
export const resolvedPolicyLabel = (policy: ResolvedPolicy | null | undefined): string | null => {
  if (!policy) return null;
  const a = policy.effective_start_date?.slice(0, 4);
  const b = policy.effective_end_date?.slice(0, 4);
  const years = !a ? null : b && b !== a ? `${a}–${b}` : a;
  return [policy.carrier_name?.trim() || "No Carrier", years].filter(Boolean).join(" ");
};

/**
 * The Policy label when the projection cannot supply resolved_policy
 * (skipped above the record bound, refused with a 409, or failed): the
 * client's newest policy by effective_start_date, which is the rule
 * review_projection.go's resolvePolicyForClient applies, so the label still
 * matches what the server would have chosen. Fetched ONLY while `enabled`;
 * a document with a projection makes no policies request (#7462 AC3).
 * Null while loading or when the client has no policy (AC4).
 */
export const useFallbackPolicyLabel = (clientId: string | undefined, enabled: boolean): string | null => {
  const { data: policies } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 10 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: enabled && !!clientId, staleTime: 60000 }
  );
  if (!enabled || !clientId || !policies?.length) return null;
  const newest = policies[0];
  return resolvedPolicyLabel({
    policy_id: String(newest.id),
    aegle_id: newest.aegle_id ?? "",
    carrier_name: getCarrierLabel(newest),
    effective_start_date: newest.effective_start_date,
    effective_end_date: newest.effective_end_date,
  });
};

/** "Uploaded Sep 26, 2026 by Dana Ortiz" — uploader resolved from the users resource. */
const useUploadedLabel = (userId: string | undefined, uploadedDatetime: string | undefined): string | null => {
  const { data: user } = useGetOne<User>(ResourceName.USERS, { id: userId ?? "" }, { enabled: !!userId });
  if (!uploadedDatetime && !userId) return null;
  const when = uploadedDatetime ? formatDate(uploadedDatetime, DATE_FORMATS.DISPLAY_LONG) : null;
  const who = user ? user.full_name || user.preferred_name || user.email : null;
  return ["Uploaded", when, who ? `by ${who}` : null].filter(Boolean).join(" ");
};

/** Inline "Label  Value" pair for the header row (#7431, artboard 1a): 13px gray label before the value. */
const InlineCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
    <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

/**
 * A header value that is a button while the reviewer can change it and plain
 * text when the header is locked (#7431 AC4). Mirrors the mockup's trigger:
 * 13px/600 text, chevron, transparent border, hover tint. The button carries
 * the design's "Change …" aria-label so the four cells read alike to a screen
 * reader.
 */
const HeaderValue = ({
  locked,
  label,
  ariaLabel,
  onClick,
  muted = false,
}: {
  locked: boolean;
  label: string;
  ariaLabel: string;
  onClick?: () => void;
  muted?: boolean;
}) => {
  if (locked || !onClick) {
    return (
      <Typography
        component="span"
        sx={{ fontSize: 13, fontWeight: 600, px: 1, color: muted ? "text.disabled" : "text.primary" }}
        noWrap
      >
        {label}
      </Typography>
    );
  }
  return (
    <HeaderTriggerButton onClick={onClick} aria-label={ariaLabel} color={muted ? "warning.dark" : "text.primary"}>
      {label}
    </HeaderTriggerButton>
  );
};

/**
 * Redesigned document-review context bar (#6099).
 *
 * A persistent band above the review grid that surfaces the three pieces a
 * reviewer needs to judge an extraction — the detected template + coverage,
 * the classification (with inline override), and the paid-through (as-of) date
 * — plus the approve/reject actions. Replaces the prior chip-row header
 * (DocumentReviewHeader) when `featureFlags.documentReviewRedesignEnabled` is on.
 *
 * Key discoverability change over the old header: the paid-through control is
 * ALWAYS present. When the document maps no carrier YTD (the common "why can't
 * I find the date?" case) it renders a muted, explained N/A state instead of
 * disappearing, so the reviewer knows the concept exists and why it's inactive.
 */
export const ReviewContextBar = ({
  recordType,
  requiresClient,
  selectedClient,
  onClientChange,
  template,
  uploadedByUserId,
  uploadedDatetime,
  readOnly = false,
  isChecking = false,
  partnerReadOnly = false,
  asOfState,
  reviewProjection,
  projectionUnavailable = false,
  classification,
  isClassificationLoading,
  isClassificationSaving,
  isClassificationReprocessing,
  classificationError,
  onClassificationOverride,
  reportedAsOf,
  onAsOfChange,
  asOfSource,
  asOfFlagged = false,
  onReviewMapping,
  onReprocess,
  excludedCount,
  isReprocessing,
  onApprove,
  onReject,
  onDeleteDocument,
  isApproving,
  isRejecting,
  isReadyForApproval,
  hasTransformation,
  isClientMissing,
  errorCount,
  unresolvedErroredCount,
  unresolvedCountIsExact,
  warningCount,
  mappingBlockingCount,
  completeness,
  mappingAdvisoryCount,
}: ReviewContextBarProps) => {
  const isReimbursementDoc = recordType === DocumentRecordType.REIMBURSEMENTS;
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  // The header LOCKS for either cause; the Approve status line names them
  // separately (readOnly → "View only…", partnerReadOnly → "Read-only
  // partner…"), the same way the sticky bar does (!4920 review).
  const locked = readOnly || partnerReadOnly;

  const {
    today,
    isInFuture: isAsOfInFuture,
    isStale: isAsOfStale,
    latestClaimPaidDate,
    latestPaidCheckPartial,
    showAsOf,
    isCarveOut,
    reportedScope,
    asOfWarningMessage,
    doubleCountWarningMessage,
    isDisabled: isAsOfDisabled,
  } = asOfState;

  // Whether the classifier produced a category (or errored) — drives the
  // "Uncategorized" fallback when there's no value for the chip to show.
  const hasClassification = !!(classification?.effective || classification?.auto_classified || classificationError);
  const categoryText = classification?.effective || classification?.auto_classified || "Uncategorized";

  const resolvedPolicy = reviewProjection?.resolved_policy ?? null;
  const fallbackPolicyLabel = useFallbackPolicyLabel(
    selectedClient?.id ? String(selectedClient.id) : undefined,
    !!projectionUnavailable && !resolvedPolicy
  );
  const policyLabel = resolvedPolicyLabel(resolvedPolicy) ?? fallbackPolicyLabel;
  const uploadedLabel = useUploadedLabel(uploadedByUserId, uploadedDatetime);
  const specificAmount =
    typeof reviewProjection?.applicable_deductible === "number" && reviewProjection.applicable_deductible > 0
      ? reviewProjection.applicable_deductible
      : null;
  const deductibleText =
    specificAmount !== null
      ? `${formatUSD(specificAmount)} ${reviewProjection?.deductible_source === "family" ? "family" : "specific"}`
      : null;

  const layoutLabel = template?.name || "Automatic";
  const asOfText = reportedAsOf ? formatDate(reportedAsOf, DATE_FORMATS.DISPLAY_LONG) : "Not set";

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: BORDER_RADIUS.XL,
        px: 2.5,
        py: 1.5,
        mb: 2,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        rowGap: 1,
      }}
    >
      {/* Left — one row of inline context, per artboard 1a: the four cells the
          reviewer sets (Client, Category, Paid through, Layout), then the
          policy the claimants are measured against, projection signals, and
          who uploaded the file. Wraps on narrow widths; nothing stacks. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          rowGap: 1,
          flexWrap: "wrap",
          minWidth: 0,
          flex: 1,
          minHeight: 36,
        }}
      >
        {requiresClient && (
          <InlineCell label="Client">
            <HeaderValue
              locked={locked}
              label={selectedClient?.client_legal_name || "Not chosen"}
              muted={!selectedClient}
              ariaLabel="Change client"
              onClick={() => setClientPickerOpen(true)}
            />
          </InlineCell>
        )}

        <InlineCell label="Category">
          {/* Settable whenever the classifier offers options — even when it
              produced no value (#7431 AC1); locked text only when the header is
              locked or there is nothing to pick from yet. */}
          {locked || !classification || isClassificationLoading ? (
            <HeaderValue
              locked
              label={isClassificationLoading ? "…" : categoryText}
              ariaLabel="Category"
              muted={!hasClassification}
            />
          ) : (
            <Box sx={{ color: "text.primary" }}>
              <ClassificationChip
                variant="text"
                classification={classification}
                isLoading={isClassificationLoading}
                isSaving={isClassificationSaving}
                isReprocessing={isClassificationReprocessing}
                error={classificationError}
                onOverride={onClassificationOverride}
              />
            </Box>
          )}
        </InlineCell>

        {/* No Paid through cell at all on a reimbursement document (#7439
            AC6, closes AB#6191): the concept does not apply, and an N/A with
            a tooltip still invited the question. A claims file that maps no
            YTD keeps the explained N/A. */}
        {!isReimbursementDoc && (
          <InlineCell label="Paid through">
            {showAsOf ? (
              locked ? (
                <HeaderValue locked label={asOfText} ariaLabel="Paid-through date" muted={!reportedAsOf} />
              ) : (
                <ReportedAsOfControl
                  variant="text"
                  value={reportedAsOf}
                  onChange={onAsOfChange}
                  fromExtraction={!!reviewProjection?.default_reported_as_of}
                  defaultAsOfSource={reviewProjection?.default_reported_as_of_source}
                  source={asOfSource}
                  flagged={asOfFlagged}
                  isInFuture={isAsOfInFuture}
                  today={today}
                  isStale={isAsOfStale}
                  staleAfterDate={latestClaimPaidDate || undefined}
                  disabled={isAsOfDisabled}
                  scope={isCarveOut ? reportedScope : undefined}
                  staleCheckPartial={latestPaidCheckPartial}
                />
              )
            ) : (
              <Tooltip
                arrow
                title="This file maps no carrier year-to-date total, so there is no reported value for a paid-through date to make authoritative. If you expected a YTD column, check the field mapping."
              >
                <span>
                  <HeaderValue locked label="N/A" ariaLabel="Paid-through date" muted />
                </span>
              </Tooltip>
            )}
          </InlineCell>
        )}

        {/* Layout — the matched template. Opens Field Mapping, which is where a
            layout's columns are read and changed today; the saved-layout picker
            (#7438) will take over this button. */}
        <InlineCell label="Layout">
          {!locked && hasTransformation && isClientMissing ? (
            <Tooltip title="Select a client first to change the layout" arrow>
              <span>
                <HeaderValue locked label={layoutLabel} muted={!template} ariaLabel="Change file layout" />
              </span>
            </Tooltip>
          ) : (
            <HeaderValue
              locked={locked || !hasTransformation}
              label={layoutLabel}
              muted={!template}
              ariaLabel="Change file layout"
              onClick={onReviewMapping}
            />
          )}
        </InlineCell>

        {/* Policy and Deductible are separate labelled facts about the SAME
            policy — the projection's resolved_policy (#7462), see
            resolvedPolicyLabel. */}
        {!isReimbursementDoc && policyLabel && (
          <InlineCell label="Policy">
            <Typography component="span" sx={{ fontSize: 13, color: "text.primary", whiteSpace: "nowrap" }}>
              {policyLabel}
            </Typography>
          </InlineCell>
        )}
        {!isReimbursementDoc && deductibleText && (
          <InlineCell label="Deductible">
            <Typography component="span" sx={{ fontSize: 13, color: "text.primary", whiteSpace: "nowrap" }}>
              {deductibleText}
            </Typography>
          </InlineCell>
        )}
        {uploadedLabel && (
          <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>
            {uploadedLabel}
          </Typography>
        )}
        {/* No policy resolved yet: fall back to the projection's deductible chip
            so the threshold is still visible. Projection advisories and the
            completeness verdict live in the sidebar (ProjectionAdvisoriesCard,
            CompletenessCard), mapping coverage on the Mapping tab — none of
            them belong in this row per artboard 1a. */}
        {!isReimbursementDoc && !resolvedPolicy && <DeductibleContextChips projection={reviewProjection ?? null} />}
        {/* View only (#7433 AC1, artboard 1f): the header is locked for this
            viewer — no role to approve, or a read-only partner. The rail's
            status line names which. */}
        {locked && (
          <Chip
            size="small"
            icon={<LockIcon sx={{ fontSize: 14 }} />}
            label="View only"
            sx={{ fontWeight: 600, fontSize: 12 }}
            data-testid="view-only-chip"
          />
        )}
      </Box>

      {/* Right — More (reprocess / reject / delete) only. Approve, its status
          line and Field mapping live in the "Before you approve" rail since
          #7432 (artboard 1a); the header keeps the destructive menu. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        <ReviewActions
          variant="more-only"
          onReviewMapping={onReviewMapping}
          onReprocess={locked ? undefined : onReprocess}
          excludedCount={excludedCount}
          isReprocessing={isReprocessing}
          onApprove={onApprove}
          onReject={onReject}
          onDeleteDocument={onDeleteDocument}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isReadyForApproval={isReadyForApproval && !isAsOfInFuture}
          hasTransformation={hasTransformation}
          isClientMissing={isClientMissing}
          readOnly={readOnly}
          partnerReadOnly={partnerReadOnly}
          isChecking={isChecking}
          isAsOfInFuture={isAsOfInFuture}
          errorCount={errorCount}
          unresolvedErroredCount={unresolvedErroredCount}
          unresolvedCountIsExact={unresolvedCountIsExact}
          warningCount={warningCount}
          mappingBlockingCount={mappingBlockingCount}
          mappingAdvisoryCount={mappingAdvisoryCount}
          asOfWarningMessage={asOfWarningMessage}
          doubleCountWarningMessage={doubleCountWarningMessage}
          completeness={completeness}
        />
      </Box>

      {requiresClient && !locked && (
        <ClientPickerDialog
          open={clientPickerOpen}
          selectedClient={selectedClient}
          onClose={() => setClientPickerOpen(false)}
          onClientChange={onClientChange}
          onRejectRequest={onReject}
        />
      )}
    </Paper>
  );
};
